import {
  Alert,
  Item,
  ItemContent,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  useInfiniteScroll,
} from '@ibsys/kit';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useInfiniteSamples } from '@/entities/sample';
import { useAppNavigate } from '@/shared/lib/ts-router';
import { cn } from '@/shared/lib';

type Props = {
  showDeleted?: boolean;
  searchQuery?: string;
};

export const SampleList: FC<Props> = ({ showDeleted, searchQuery }) => {
  const { t } = useTranslation('samples');
  const { id } = useParams<{ id?: string }>();
  const navigate = useAppNavigate();
  const viewportRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteSamples({
    isDeleted: showDeleted ? undefined : false,
    search: searchQuery || undefined,
    limit: 50,
  });

  const samples = data?.samples ?? [];
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 500,
    root: viewportRef,
  });

  const handleSelect = (sampleId: number) => {
    navigate('/samples/:id', { id: sampleId.toString() });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    );
  }

  if (!samples.length) {
    return <Alert className="text-muted-foreground p-4" title={t('listIsEmpty')} />;
  }

  return (
    <ScrollArea viewPortProps={{ ref: viewportRef }}>
      <ul className="flex flex-col gap-2">
        {samples.map((sample) => {
          const isActive = id === sample.id.toString();
          return (
            <li key={sample.id} onClick={() => handleSelect(sample.id)}>
              <Item
                variant={isActive ? 'muted' : 'outline'}
                className={cn('hover:bg-muted cursor-pointer overflow-x-hidden', sample.isDeleted && 'opacity-50')}
              >
                <ItemHeader>
                  <ItemTitle className="text-sm font-medium">{sample.name}</ItemTitle>
                </ItemHeader>
                <ItemContent className="text-xs text-muted-foreground">
                  {t('conversationCount', { count: sample.conversationCount })}
                </ItemContent>
              </Item>
            </li>
          );
        })}
        <Sentinel ref={sentinelRef}>
          {isFetchingNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
      </ul>
    </ScrollArea>
  );
};
